import { of } from 'rxjs';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, catchError, exhaustMap, switchMap } from 'rxjs/operators';
import { combineLatest } from 'rxjs';

import * as actions from '@appStore/analyze/actions';
import { AnalyzeActionTypes } from '@appStore/analyze/actions';
import { AnalyzeService } from '@appStore/analyze/services';
import { AnalyzeReadingResult, ConsumptionData, MeterReadingData, ConsumptionLoaderConfiguration } from '@model';
import {
  AnalyzeConsumptionType,
  DatePeriodType,
  AnalyzeMeterReadingType,
  AnalyzeCompareWith,
  AnalyzeUnitType,
  AnalyzeSettingsType,
  MeteringType
} from '@enums';

@Injectable()
export class AnalyzeEffects {
  constructor(private analyzeService: AnalyzeService, private actions$: Actions) {}

  loadConsumptionData$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AnalyzeActionTypes.AnalyzeLoadConsumptionData),
      switchMap((action: actions.AnalyzeLoadConsumptionData) => {
        let settings = { ...action.payload.paneSettings };
        let unit = action.payload.unit;
        let period = action.payload.period;
        let meteringPointIds = [...settings.meteringPointIds];
  
        let compareYears = settings.compareYears;
        if (!compareYears || (period === DatePeriodType.Hour && settings.settingsType !== AnalyzeSettingsType.Effect)) {
          compareYears = [];
        }
  
        if (action.payload.type === AnalyzeConsumptionType.Power) {
          meteringPointIds = [settings.explicitMeteringPointId];
        }
  
        if (
          action.payload.type === AnalyzeConsumptionType.Evmrt ||
          action.payload.type === AnalyzeConsumptionType.ReturnTemp
        ) {
          if (action.payload.period === DatePeriodType.Hour) {
            meteringPointIds = [settings.explicitMeteringPointId];
          }
  
          if (settings.displaySupplyTemp && action.payload.type === AnalyzeConsumptionType.ReturnTemp) {
            return this.getReturnTempAndSupplyTempReadings(action.payload, meteringPointIds, period);
          }
  
          return this.getMeterReadingAsConsumption(
            action.payload,
            compareYears,
            meteringPointIds,
            period,
            action.payload.type
          );
        }
        if (action.payload.type === AnalyzeConsumptionType.Economy) {
          return this.getEcononmyConsumption(action.payload, compareYears, meteringPointIds, period, unit);
        }
  
        return this.getDefaultConsumption(action.payload, compareYears, meteringPointIds, period, unit);
      })
    );
  });
 
  loadMeterReadingCompareYearData$ = createEffect(() => {
    return  this.actions$.pipe(
      ofType(AnalyzeActionTypes.AnalyzeLoadMeterReadingData),
      switchMap((action: actions.AnalyzeLoadMeterReadingData) => {
        let settings = { ...action.payload.paneSettings };
        let period = action.payload.period;
        let type: AnalyzeMeterReadingType;
  
        let compareYears = settings.compareYears;
        if (!compareYears || (period === DatePeriodType.Hour && settings.settingsType !== AnalyzeSettingsType.Effect)) {
          compareYears = [];
        }
  
        switch (settings.compareWith) {
          case AnalyzeCompareWith.ReturnTemp:
            type = AnalyzeMeterReadingType.ReturnTemp;
            break;
          case AnalyzeCompareWith.SupplyTemp:
            type = AnalyzeMeterReadingType.SupplyTemp;
            break;
          case AnalyzeCompareWith.Humidity:
            type = AnalyzeMeterReadingType.Humidity;
            break;
          case AnalyzeCompareWith.OutdoorTemp:
            type = AnalyzeMeterReadingType.OutdoorTemp;
            break;
          case AnalyzeCompareWith.Evmrt:
            type = AnalyzeMeterReadingType.Evmrt;
            break;
        }
  
        return this.getDefaultMeterReadings(action.payload, compareYears, settings.meteringPointIds, period, type);
      })
    );
  
  });
 
  getTemperatureData$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AnalyzeActionTypes.AnalyzeLoadTemperatureData),
      map((action: actions.AnalyzeLoadTemperatureData) => action.payload),
      switchMap(payload => {
        return this.analyzeService.getTemperatureData(payload.masterMeteringPointId, payload.meteringPointIds).pipe(
          map(items => new actions.AnalyzeLoadTemperatureDataSuccess(items)),
          catchError(error => of(new actions.AnalyzeLoadTemperatureDataFail(error)))
        );
      })
    ); 
  });

  createReportKey$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AnalyzeActionTypes.CreateReportKey),
      exhaustMap(action => {
        return this.analyzeService.createReportKey().pipe(
          map(key => new actions.CreateReportKeySuccess(key)),
          catchError(error => of(new actions.CreateReportKeyFail(error)))
        );
      })
    );
  });

  getMeterReadingAsConsumption = (
    loadConfig: ConsumptionLoaderConfiguration,
    compareYears: number[],
    meteringPointIds: number[],
    period: DatePeriodType,
    type: string
  ) => {
    let settings = { ...loadConfig.paneSettings };

    let meteringTypeToLoad = this.getMeteringTypeToLoad(loadConfig.paneSettings.meteringType);

    return this.analyzeService
      .getMeterReadings(
        loadConfig.paneSettings.masterMeteringPointId,
        meteringPointIds,
        loadConfig.dateFrom,
        loadConfig.dateTo,
        compareYears,
        period,
        type,
        meteringTypeToLoad,
        loadConfig.paneSettings.excludeWeekends
      )
      .pipe(
        map(
          (data: AnalyzeReadingResult[]) =>
            new actions.AnalyzeLoadConsumptionSuccess(<ConsumptionData>{
              period: period,
              paneSettings: settings,
              consumptionData: data,
              dateFrom: loadConfig.dateFrom,
              dateTo: loadConfig.dateTo
            })
        ),
        catchError(error => of(new actions.AnalyzeLoadConsumptionFail(error)))
      );
  };

  getDefaultMeterReadings = (
    loadConfig: ConsumptionLoaderConfiguration,
    compareYears: number[],
    meteringPointIds: number[],
    period: DatePeriodType,
    type: string
  ) => {
    let settings = { ...loadConfig.paneSettings };

    let meteringTypeToLoad = this.getMeteringTypeToLoad(loadConfig.paneSettings.meteringType);

    return this.analyzeService
      .getMeterReadings(
        loadConfig.paneSettings.masterMeteringPointId,
        meteringPointIds,
        loadConfig.dateFrom,
        loadConfig.dateTo,
        compareYears,
        period,
        type,
        meteringTypeToLoad,
        loadConfig.paneSettings.excludeWeekends
      )
      .pipe(
        map(
          (data: AnalyzeReadingResult[]) =>
            new actions.AnalyzeLoadMeterReadingSuccess(<MeterReadingData>{
              period: period,
              paneSettings: settings,
              meterReadingData: data,
              dateFrom: loadConfig.dateFrom,
              dateTo: loadConfig.dateTo
            })
        ),
        catchError(error => of(new actions.AnalyzeLoadMeterReadingFail(error)))
      );
  };

  getDefaultConsumption = (
    loadConfig: ConsumptionLoaderConfiguration,
    compareYears: number[],
    meteringPointIds: number[],
    period: DatePeriodType,
    unit: AnalyzeUnitType
  ) => {
    let settings = { ...loadConfig.paneSettings };
    let meteringTypeToLoad = this.getMeteringTypeToLoad(loadConfig.paneSettings.meteringType);

    return this.analyzeService
      .getConsumptions(
        loadConfig.paneSettings.masterMeteringPointId,
        meteringPointIds,
        loadConfig.dateFrom,
        loadConfig.dateTo,
        compareYears,
        period,
        loadConfig.type,
        unit,
        meteringTypeToLoad,
        loadConfig.paneSettings.excludeWeekends,
        loadConfig.paneSettings.effctSignatureLine
      )
      .pipe(
        map(
          (data: AnalyzeReadingResult[]) =>
            new actions.AnalyzeLoadConsumptionSuccess(<ConsumptionData>{
              period: period,
              paneSettings: settings,
              consumptionData: data,
              dateFrom: loadConfig.dateFrom,
              dateTo: loadConfig.dateTo
            })
        ),
        catchError(error => of(new actions.AnalyzeLoadConsumptionFail(error)))
      );
  };

  getReturnTempAndSupplyTempReadings = (
    loadConfig: ConsumptionLoaderConfiguration,
    meteringPointIds: number[],
    period: DatePeriodType
  ) => {
    let settings = { ...loadConfig.paneSettings };

    return combineLatest(
      this.analyzeService.getMeterReadings(
        loadConfig.paneSettings.masterMeteringPointId,
        meteringPointIds,
        loadConfig.dateFrom,
        loadConfig.dateTo,
        settings.compareYears,
        period,
        loadConfig.type,
        loadConfig.paneSettings.meteringType === MeteringType.ColdLogical
          ? MeteringType.Cold
          : loadConfig.paneSettings.meteringType === MeteringType.HotLogical
          ? MeteringType.Hot
          : loadConfig.paneSettings.meteringType,
        loadConfig.paneSettings.excludeWeekends
      ),
      this.analyzeService.getMeterReadings(
        loadConfig.paneSettings.masterMeteringPointId,
        meteringPointIds,
        loadConfig.dateFrom,
        loadConfig.dateTo,
        settings.compareYears,
        period,
        AnalyzeConsumptionType.SupplyTemp,
        loadConfig.paneSettings.meteringType === MeteringType.ColdLogical
          ? MeteringType.Cold
          : loadConfig.paneSettings.meteringType === MeteringType.HotLogical
          ? MeteringType.Hot
          : loadConfig.paneSettings.meteringType,
        loadConfig.paneSettings.excludeWeekends
      ),
      (data1, data2) => ({ data1, data2 })
    ).pipe(
      map(
        data =>
          new actions.AnalyzeLoadConsumptionSuccess(<ConsumptionData>{
            period: period,
            paneSettings: settings,
            consumptionData: [...data.data1, ...data.data2],
            dateFrom: loadConfig.dateFrom,
            dateTo: loadConfig.dateTo
          })
      ),
      catchError(error => of(new actions.AnalyzeLoadConsumptionFail(error)))
    );
  };

  getMeteringTypeToLoad = (meteringType: MeteringType): MeteringType => {
    if (meteringType === MeteringType.HotLogical) {
      return MeteringType.Hot;
    }
    if (meteringType === MeteringType.ColdLogical) {
      return MeteringType.Cold;
    }
    return meteringType;
  };

  getEcononmyConsumption = (
    loadConfig: ConsumptionLoaderConfiguration,
    compareYears: number[],
    meteringPointIds: number[],
    period: DatePeriodType,
    unit: AnalyzeUnitType
  ) => {
    let settings = { ...loadConfig.paneSettings };

    return this.analyzeService
      .getEcononmyConsumption(meteringPointIds, loadConfig.dateFrom, loadConfig.dateTo, period, unit, compareYears, settings.meteringType)
      .pipe(
        map(
          (data: AnalyzeReadingResult[]) =>
            new actions.AnalyzeLoadConsumptionSuccess(<ConsumptionData>{
              period: period,
              paneSettings: settings,
              consumptionData: data,
              dateFrom: loadConfig.dateFrom,
              dateTo: loadConfig.dateTo
            })
        ),
        catchError(error => of(new actions.AnalyzeLoadConsumptionFail(error)))
      );
  };
}
